import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import {
  getColoresMuestras,
  updateColorMuestra,
} from "../../api/Muestras/muestraApi";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import GridViewIcon from "@mui/icons-material/GridView";
import ListIcon from "@mui/icons-material/List";
import DrawerColores from "../Colores/DrawerColores";

const ColoresMuestras = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);

  const [colores, setColores] = useState([]);
  const [filteredColores, setFilteredColores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("orden");
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null);
  const [viewMode, setViewMode] = useState("grid");

  const hexToHue = (hex) => {
    if (!hex || hex.length < 7) return 0;
    let r = parseInt(hex.slice(1, 3), 16) / 255;
    let g = parseInt(hex.slice(3, 5), 16) / 255;
    let b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    if (max !== min) {
      const d = max - min;
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
        default:
          break;
      }
      h /= 6;
    }
    return h * 360;
  };

  const sortColors = (list, criteria) => {
    switch (criteria) {
      case "hex":
        return [...list].sort((a, b) => a.HEX?.localeCompare(b.HEX || ""));
      case "hue":
        return [...list].sort((a, b) => hexToHue(a.HEX) - hexToHue(b.HEX));
      case "pantone":
        return [...list].sort((a, b) =>
          a.PANTONE?.localeCompare(b.PANTONE || ""),
        );
      default:
        return [...list].sort(
          (a, b) => (a.ORDEN || 0) - (b.ORDEN || 0) || a.PANTONE.localeCompare(b.PANTONE),
        );
    }
  };

  const handleColorClick = (color) => {
    setSelectedColor(color);
    setOpenDrawer(true);
  };

  const handleSaveColor = async () => {
    if (!selectedColor?.ID_COLOR) return;

    try {
      const saved = await updateColorMuestra(
        selectedColor.ID_COLOR,
        selectedColor,
      );
      const updatedList = colores.map((c) =>
        c.ID_COLOR === selectedColor.ID_COLOR ? saved : c,
      );
      setColores(updatedList);
      setFilteredColores(sortColors(updatedList, sortBy));
      setOpenDrawer(false);
    } catch (error) {
      console.error("Error al guardar color de muestra:", error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/login");

    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      if (decodedToken.exp < currentTime) {
        localStorage.removeItem("token");
        navigate("/login");
        return;
      }

      const fetchData = async () => {
        try {
          const data = await getColoresMuestras();
          const sorted = sortColors(data, sortBy);
          setColores(sorted);
          setFilteredColores(sorted);
        } catch (err) {
          console.error("Error al cargar colores de muestras:", err);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    } catch (error) {
      console.error("Error decoding token:", error);
      navigate("/login");
    }
  }, [navigate, sortBy]);

  useEffect(() => {
    const savedView = localStorage.getItem("muestraColorViewMode");
    if (savedView) setViewMode(savedView);
  }, []);

  useEffect(() => {
    localStorage.setItem("muestraColorViewMode", viewMode);
  }, [viewMode]);

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    if (!term) return setFilteredColores(sortColors(colores, sortBy));
    setFilteredColores(
      sortColors(
        colores.filter(
          (c) =>
            c.COLOR?.toLowerCase().includes(term) ||
            c.PANTONE?.toLowerCase().includes(term),
        ),
        sortBy,
      ),
    );
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    setFilteredColores(sortColors(colores, e.target.value));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ ...contentMargin, p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          position: "sticky",
          top: 16,
          backgroundColor: theme.palette.background.paper,
          zIndex: 10,
          py: 2,
          px: 3,
          borderRadius: 2,
          boxShadow: theme.shadows[1],
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Colores Muestras
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Catálogo simplificado para muestras ({colores.length} colores)
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField
            label="Buscar color..."
            variant="outlined"
            size="small"
            onChange={handleSearch}
            sx={{ flex: 1, minWidth: 280 }}
          />

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Ordenar por</InputLabel>
            <Select value={sortBy} onChange={handleSortChange} label="Ordenar por">
              <MenuItem value="orden">Orden catálogo</MenuItem>
              <MenuItem value="pantone">Nombre (A–Z)</MenuItem>
              <MenuItem value="hex">HEX</MenuItem>
              <MenuItem value="hue">Tono visual</MenuItem>
            </Select>
          </FormControl>

          <Tooltip
            title={viewMode === "grid" ? "Ver como lista" : "Ver como cuadrícula"}
          >
            <IconButton
              onClick={() =>
                setViewMode((prev) => (prev === "grid" ? "list" : "grid"))
              }
              sx={{
                border: "1px solid #90b2b7",
                backgroundColor: theme.palette.background.paper,
                color: "#90b2b7",
                "&:hover": { backgroundColor: "#90b2b7", color: "#fff" },
              }}
            >
              {viewMode === "grid" ? <ListIcon /> : <GridViewIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ height: "calc(100vh - 80px)", overflowY: "auto", pr: 1 }}>
        {viewMode === "grid" ? (
          <Grid container spacing={2}>
            {filteredColores.map((color) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={color.ID_COLOR}>
                <Paper
                  onClick={() => handleColorClick(color)}
                  elevation={2}
                  sx={{
                    cursor: "pointer",
                    p: 1.5,
                    textAlign: "center",
                    borderRadius: 2,
                    transition: "0.2s",
                    "&:hover": { transform: "scale(1.04)" },
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: color.HEX,
                      height: 70,
                      borderRadius: 1.5,
                      border: "1px solid rgba(0,0,0,0.1)",
                      mb: 1,
                    }}
                  />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {color.PANTONE || "—"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "#777",
                      fontFamily: "monospace",
                    }}
                  >
                    {color.HEX}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>HEX</TableCell>
                <TableCell>RGB</TableCell>
                <TableCell>Preview</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredColores.map((color) => (
                <TableRow
                  key={color.ID_COLOR}
                  hover
                  onClick={() => handleColorClick(color)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{color.PANTONE}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>{color.HEX}</TableCell>
                  <TableCell>
                    RGB({color.RED}, {color.GREEN}, {color.BLUE})
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        width: 30,
                        height: 20,
                        backgroundColor: color.HEX,
                        borderRadius: 1,
                        border: "1px solid rgba(0,0,0,0.1)",
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      <DrawerColores
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        color={selectedColor}
        onChange={setSelectedColor}
        onSave={handleSaveColor}
      />
    </Box>
  );
};

export default ColoresMuestras;
